import { useMutation } from '@tanstack/react-query';
import { Flex, Form, Space, Table, Typography } from 'antd';
import { useForm } from 'antd/es/form/Form';
import FormItem from 'antd/es/form/FormItem';
import dayjs from 'dayjs';
import {
  ChangeEvent,
  Dispatch,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { IGetPurchaseInvoiceMaterials } from 'apis/GraphQL/Variables/purchaseInvoice';
import { PurChaseInvoiceApi } from 'apis/PurchaseInvoiceApis';
import { EditIcon, XIcon } from 'assets/svg';
import { Button, ButtonAction, InputForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { TpurchaseDetail, TPurchaseDetail } from 'interfaces/PurchaseInvoice';
import { SavePurchaseOrder } from 'interfaces/PurchaseInvoice/AddPurchaseInvoice';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import formatNumber, {
  formatCurrency,
  formatDate,
  totalData,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { formatValue } from '../PurchaseInvoiceDetailPage/TablePurchaseInvoiceDetail';

interface IProps {
  data: any[];
  purchaseOrderData: SavePurchaseOrder | undefined;
  editIndex: {
    index: number;
    type: string;
  };
  onAdd: () => void;
  onSave: (data: any[]) => void;
  onEdit: (data: any[]) => void;
  onDelete: (data: any[]) => void;
  setTableData: Dispatch<any>;
  setEditIndex: Dispatch<
    React.SetStateAction<{
      index: number;
      type: string;
    }>
  >;
  migoList?: any[];
  lastMigoStatus?: boolean;
  lastMigoType?: string;
}

interface IForm {
  tproductId: string;
  measureWeightThread: string;
  lossWeightThread: string;
  includeWeightThread: string;
  measureWeightThreadAccounting: string;
  lossWeightThreadAccounting: string;
  includeWeightThreadAccounting: string;
  sloc: string;
}

const { Text } = Typography;
const { Summary } = Table;
const { SaveTwoTone, CloseCircleTwoTone, PlusOutlined } = icons;

export const totalKeys: (keyof TpurchaseDetail)[] = [
  'totalGoldWeightThread',
  'q10',
  'measureWeightThread',
  'lossWeightThread',
  'includeWeightThread',
  'totalGoldWeightOfStool',
  'finalPrice',
  'totalWeightThreadAccounting',
  'measureWeightThreadAccounting',
  'lossWeightThreadAccounting',
  'includeWeightThreadAccounting',
  'q10Accounting',
  'totalWeightStoolAccounting',
  'finalPriceAccounting',
];

export const dataIndexes: (keyof TpurchaseDetail)[] = [
  'totalGoldWeightThread',
  'q10',
  'measureWeightThread',
  'lossWeightThread',
  'includeWeightThread',
  'totalGoldWeightOfStool',
  'unitPrice',
  'finalPrice',
  'policyPrice',
  'totalWeightThreadAccounting',
  'measureWeightThreadAccounting',
  'lossWeightThreadAccounting',
  'includeWeightThreadAccounting',
  'q10Accounting',
  'totalWeightStoolAccounting',
  'finalPriceAccounting',
];

const TablePurchaseInvoiceDetail = ({
  data,
  editIndex,
  purchaseOrderData,
  onAdd,
  onSave,
  onEdit,
  onDelete,
  setTableData,
  setEditIndex,
  migoList,
  lastMigoStatus,
  lastMigoType,
}: IProps) => {
  const { user } = useUser();

  // Debug props
  console.log('TableAddPurchaseInvoice props:', {
    migoList,
    lastMigoStatus,
    lastMigoType,
    migoListLength: migoList?.length,
    migoListRef: migoList, // Array reference
    shouldDisable:
      migoList &&
      migoList.length > 0 &&
      lastMigoStatus === true &&
      lastMigoType === '501',
    timestamp: new Date().toLocaleTimeString(), // Thêm timestamp để track re-render
  });
  const [form] = useForm<IForm>();

  const materialRef = useRef<HTMLDivElement>(null);
  const measureWeightRef = useRef<HTMLDivElement>(null);
  const measureWeightAccountantRef = useRef<HTMLDivElement>(null);

  const [materials, setMaterials] =
    useState<ILazySelectData>(initLazySelectData);

  const queryParam = useQueryParam(),
    page = parseInt(queryParam.get('page') + '') || 1,
    pageSize = parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const { mutate: getPurchaseInvoiceMaterials } = useMutation({
    mutationFn: (params: IGetPurchaseInvoiceMaterials) =>
      PurChaseInvoiceApi.getPurchaseInvoiceMaterials(params),
    onSuccess: (res) => {
      const items = res?.data?.getProduct?.items;

      setMaterials({
        options: items?.map((item: any) => ({
          label: `${item?.id} - ${item?.name}`,
          value: item?.id,
        })),
        totalItems: res?.data?.getProduct?.pageInfo?.totalItems,
      });
    },
  });

  useEffect(() => {
    if (editIndex.index === -1) return;

    if (editIndex.type === '' && materialRef.current) {
      materialRef.current?.scrollIntoView({
        inline: 'start',
        block: 'start',
        behavior: 'smooth',
      });
    }

    if (
      measureWeightAccountantRef.current &&
      editIndex.type === 'edit' &&
      user?.id !== purchaseOrderData?.createId
    ) {
      measureWeightAccountantRef.current?.scrollIntoView({
        inline: 'start',
        block: 'start',
        behavior: 'smooth',
      });
    }

    if (
      measureWeightRef.current &&
      editIndex.type === 'edit' &&
      user?.id === purchaseOrderData?.createId
    ) {
      measureWeightRef.current?.scrollIntoView({
        inline: 'start',
        block: 'start',
        behavior: 'smooth',
      });
    }
  }, [
    user,
    editIndex,
    purchaseOrderData,
    materialRef,
    measureWeightRef,
    measureWeightAccountantRef,
  ]);

  const formatToNumber = (value: string) => {
    const formattedString = value?.replace(/\./g, '')?.replace(',', '.');
    return parseFloat(formattedString);
  };

  const handleWeightChange = (
    field: keyof IForm,
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;
    if (/[a-zA-Z\u00C0-\u017F]/.test(value.replace(/,/g, ''))) {
      return;
    }

    const formattedValue = value.replace(/[^0-9,]/g, '');
    const [integerPart, decimalPart] = formattedValue.split(',');

    const formattedIntegerPart = integerPart
      ? parseInt(integerPart, 10).toLocaleString('vi-VN')
      : '';

    const formattedDecimalPart =
      decimalPart !== undefined ? `,${decimalPart.slice(0, 4)}` : '';
    const fieldValue = formattedIntegerPart + formattedDecimalPart;

    form.setFieldValue(field, fieldValue);
  };

  const handleScrollCustomersChange = useCallback(
    (params: ILazySelectParams) => {
      const { pageSize, value } = params;

      getPurchaseInvoiceMaterials({
        pageSize,
        value,
      });
    },
    [],
  );

  const columns: ColumnsTypeCustom = useMemo(
    () => [
      {
        title: 'STT',
        render: (_, __, index: number) => {
          const isEdit = editIndex.index === index && editIndex.type !== 'edit';
          return (
            <div ref={isEdit ? materialRef : null}>
              {index + 1 + pageSize * (page - 1)}
            </div>
          );
        },
      },
      {
        align: 'right',
        title: 'Sloc',
        dataIndex: 'sloc',
        render: (value, _, index) =>
          editIndex.index === index &&
          (editIndex.type === '' ||
            user?.id === purchaseOrderData?.createId) ? (
            <Form.Item
              className="mb-0"
              name="sloc"
              rules={[
                { required: true, message: 'Vui lòng nhập Sloc' },
                {
                  pattern: /^[^a-zA-Z\u00C0-\u017F]*$/,
                  message: 'Chỉ được nhập số',
                },
              ]}
            >
              <InputForm
                onChange={(e) => {
                  form.setFieldValue('sloc', e.target.value);
                }}
              />
            </Form.Item>
          ) : value ? (
            value
          ) : (
            '-'
          ),
      },
      {
        width: 470,
        title: 'Mã NVL/Tên NVL',
        dataIndex: ['tproduct', 'id'],
        render: (_, record, index) =>
          editIndex.index === index && editIndex.type !== 'edit' ? (
            <FormItem
              className="mb-0"
              name="tproductId"
              rules={[
                { required: true, message: 'Vui lòng chọn NVL' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();

                    // const isExistedItem = !!data?.filter(
                    //   (item: any) => item?.tproduct?.id === value,
                    // )?.length;

                    // if (isExistedItem)
                    //   return Promise.reject(
                    //     'Dẻ đã tồn tại, vui lòng chỉnh sửa dẻ đã thêm',
                    //   );

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <LazySelectForm
                data={materials}
                onSelectScrollChange={handleScrollCustomersChange}
              />
            </FormItem>
          ) : (
            `${record?.tproduct?.id} - ${record?.tproduct?.name}`
          ),
      },
      // {
      //   title: 'Tên NVL',
      //   dataIndex: ['tproduct', 'name'],
      // },
      {
        align: 'right',
        title: 'TL(chỉ)',
        dataIndex: 'totalGoldWeightThread',
        render: (value) =>
          formatCurrency({
            value: Number(Number(value).toFixed(4)),
            format: 4,
          }) || '_',
      },

      {
        align: 'right',
        title: 'Vàng Q10 (chỉ)',
        dataIndex: 'q10',
        render: (value) =>
          formatCurrency({
            value: Number(Number(value).toFixed(4)),
            format: 4,
          }) || '_',
      },
      {
        width: 300,
        align: 'right',
        title: 'TL BAO GỒM HỘT(chỉ)',
        dataIndex: 'measureWeightThread',
        render: (value, _, index) => {
          const isEdit =
            editIndex.index === index &&
            (editIndex.type === '' || user?.id === purchaseOrderData?.createId);

          return (
            <div ref={isEdit ? measureWeightRef : null}>
              {isEdit ? (
                <FormItem
                  className="mb-0"
                  name="measureWeightThread"
                  rules={[
                    { required: true, message: 'Vui lòng nhập TL' },
                    {
                      pattern: /^[^a-zA-Z\u00C0-\u017F]*$/,
                      message: 'Chỉ được nhập số',
                    },
                    {
                      validator: (_, measureWeightThread) => {
                        const measureWeight =
                          formatToNumber(measureWeightThread);
                        const lossWeight = formatToNumber(
                          form.getFieldValue('lossWeightThread') || '0',
                        );
                        const includeWeight = formatToNumber(
                          form.getFieldValue('includeWeightThread') || '0',
                        );

                        const totalWeight = lossWeight + includeWeight;
                        // console.log(totalWeight, measureWeight);

                        if (measureWeight && totalWeight > measureWeight) {
                          return Promise.reject(
                            'Tổng TL bụi bẩn và TL HỘT không được lớn hơn TL BAO GỒM HỘT.',
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputForm
                    onChange={(e) =>
                      handleWeightChange('measureWeightThread', e)
                    }
                  />
                </FormItem>
              ) : (
                formatCurrency({
                  value: Number(Number(value).toFixed(4)),
                  format: 4,
                })
              )}
            </div>
          );
          // return isEdit ? (
          //   // editIndex.index === index &&
          //   //   (editIndex.type === '' ||
          //   //     user?.id === purchaseOrderData?.createId)
          //   <FormItem
          //     className="mb-0"
          //     name="measureWeightThread"
          //     rules={[
          //       { required: true, message: 'Vui lòng nhập TL' },
          //       {
          //         pattern: /^[^a-zA-Z\u00C0-\u017F]*$/,
          //         message: 'Chỉ được nhập số',
          //       },
          //       {
          //         validator: (_, measureWeightThread) => {
          //           const measureWeight = formatToNumber(measureWeightThread);
          //           const lossWeight = formatToNumber(
          //             form.getFieldValue('lossWeightThread') || '0',
          //           );
          //           const includeWeight = formatToNumber(
          //             form.getFieldValue('includeWeightThread') || '0',
          //           );

          //           const totalWeight = lossWeight + includeWeight;
          //           // console.log(totalWeight, measureWeight);

          //           if (measureWeight && totalWeight > measureWeight) {
          //             return Promise.reject(
          //               'Tổng TL bụi bẩn và TL HỘT không được lớn hơn TL BAO GỒM HỘT.',
          //             );
          //           }

          //           return Promise.resolve();
          //         },
          //       },
          //     ]}
          //   >
          //     <InputForm
          //       onChange={(e) => handleWeightChange('measureWeightThread', e)}
          //     />
          //   </FormItem>
          // ) : (
          //   formatValue(value)
          // );
        },
      },
      {
        align: 'right',
        title: 'TL bụi bẩn (chỉ)',
        dataIndex: 'lossWeightThread',
        // render: (value) => formatValue(value),
        render: (value, _, index) => {
          return editIndex.index === index &&
            (editIndex.type === '' ||
              user?.id === purchaseOrderData?.createId) ? (
            // return editIndex.index === index ? (
            <FormItem
              className="mb-0"
              name="lossWeightThread"
              rules={[
                { required: true, message: 'Vui lòng nhập TL' },
                {
                  pattern: /^[^a-zA-Z\u00C0-\u017F]*$/,
                  message: 'Chỉ được nhập số',
                },
              ]}
            >
              <InputForm
                onChange={(e) => handleWeightChange('lossWeightThread', e)}
              />
            </FormItem>
          ) : (
            formatCurrency({
              value: Number(Number(value).toFixed(4)),
              format: 4,
            })
          );
        },
      },
      {
        align: 'right',
        title: 'TL HỘT(chỉ)',
        dataIndex: 'includeWeightThread',
        // render: (value) => formatValue(value),
        render: (value, _, index) => {
          return editIndex.index === index &&
            (editIndex.type === '' ||
              user?.id === purchaseOrderData?.createId) ? (
            // return editIndex.index === index ? (
            <FormItem
              className="mb-0"
              name="includeWeightThread"
              rules={[
                { required: true, message: 'Vui lòng nhập TL' },
                {
                  pattern: /^[^a-zA-Z\u00C0-\u017F]*$/,
                  message: 'Chỉ được nhập số',
                },
              ]}
            >
              <InputForm
                onChange={(e) => handleWeightChange('includeWeightThread', e)}
              />
            </FormItem>
          ) : (
            formatCurrency({
              value: Number(Number(value).toFixed(4)),
              format: 4,
            })
          );
        },
      },
      {
        align: 'right',
        title: 'TL (phân)',
        dataIndex: 'totalGoldWeightOfStool',
        render: (value) =>
          formatCurrency({
            value: Number(Number(value).toFixed(4)),
            format: 4,
          }) || '_',
      },
      {
        width: 100,
        align: 'right',
        title: 'Đơn giá (phân)',
        dataIndex: 'unitPrice',
        render: (value) =>
          value === null || value === undefined
            ? '-'
            : new Intl.NumberFormat('vi-VN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(value),
      },
      {
        width: 150,
        align: 'right',
        currency: 'vi-VN',
        title: 'Thành tiền (phân)',
        dataIndex: 'finalPrice',
        render: (value) => formatNumber(value),
      },
      {
        width: 150,
        align: 'right',
        currency: 'vi-VN',
        title: 'Tiền chính sách',
        dataIndex: 'policyPrice',
        render: (value) => formatNumber(value),
      },
      {
        align: 'right',
        title: 'TL sau ĐC(chỉ)',
        dataIndex: 'totalWeightThreadAccounting',
        render: (value) =>
          formatCurrency({
            value: Number(Number(value).toFixed(4)),
            format: 4,
          }) || '_',
        // render: (value) => (value ? formatCurrency({ value, format: 3 }) : '-'),
      },
      {
        width: 300,
        align: 'right',
        title: 'TL bao gồm hột ĐC(chỉ)',
        dataIndex: 'measureWeightThreadAccounting',
        render: (value, _, index) => {
          const isEdit =
            editIndex.index === index &&
            editIndex.type === 'edit' &&
            user?.id !== purchaseOrderData?.createId;

          return (
            <div ref={isEdit ? measureWeightAccountantRef : null}>
              {isEdit ? (
                <Form.Item
                  className="mb-0"
                  name="measureWeightThreadAccounting"
                  rules={[
                    { required: true, message: 'Vui lòng nhập TL' },
                    {
                      pattern: /^[^a-zA-Z\u00C0-\u017F]*$/,
                      message: 'Chỉ được nhập số',
                    },
                    {
                      validator: (_, measureWeightThreadAccounting) => {
                        const measureWeight = formatToNumber(
                          measureWeightThreadAccounting,
                        );
                        const lossWeight = formatToNumber(
                          form.getFieldValue('lossWeightThreadAccounting') ||
                            '0',
                        );
                        const includeWeight = formatToNumber(
                          form.getFieldValue('includeWeightThreadAccounting') ||
                            '0',
                        );

                        const totalWeight = lossWeight + includeWeight;

                        if (measureWeight && totalWeight > measureWeight) {
                          return Promise.reject(
                            'Tổng TL bụi bẩn và TL HỘT không được lớn hơn TL BAO GỒM HỘT.',
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputForm
                    onChange={(e) =>
                      handleWeightChange('measureWeightThreadAccounting', e)
                    }
                  />
                </Form.Item>
              ) : value ? (
                formatCurrency({
                  value: Number(Number(value).toFixed(4)),
                  format: 4,
                })
              ) : (
                '-'
              )}
            </div>
          );
        },
        // render: (value, _, index) =>
        //   editIndex.index === index &&
        //   editIndex.type === 'edit' &&
        //   user?.id !== purchaseOrderData?.createId ? (
        //     <Form.Item
        //       className="mb-0"
        //       name="measureWeightThreadAccounting"
        //       rules={[
        //         { required: true, message: 'Vui lòng nhập TL' },
        //         {
        //           pattern: /^[^a-zA-Z\u00C0-\u017F]*$/,
        //           message: 'Chỉ được nhập số',
        //         },
        //         {
        //           validator: (_, measureWeightThreadAccounting) => {
        //             const measureWeight = formatToNumber(
        //               measureWeightThreadAccounting,
        //             );
        //             const lossWeight = formatToNumber(
        //               form.getFieldValue('lossWeightThreadAccounting') || '0',
        //             );
        //             const includeWeight = formatToNumber(
        //               form.getFieldValue('includeWeightThreadAccounting') ||
        //                 '0',
        //             );

        //             const totalWeight = lossWeight + includeWeight;

        //             if (measureWeight && totalWeight > measureWeight) {
        //               return Promise.reject(
        //                 'Tổng TL bụi bẩn và TL HỘT không được lớn hơn TL BAO GỒM HỘT.',
        //               );
        //             }

        //             return Promise.resolve();
        //           },
        //         },
        //       ]}
        //     >
        //       <InputForm
        //         onChange={(e) =>
        //           handleWeightChange('measureWeightThreadAccounting', e)
        //         }
        //       />
        //     </Form.Item>
        //   ) : value ? (
        //     formatValue(value)
        //   ) : (
        //     '-'
        //   ),
      },
      {
        align: 'right',
        title: 'TL bụi bẩn ĐC(chỉ)',
        dataIndex: 'lossWeightThreadAccounting',
        // render: (value) => formatNumber(value),
        render: (value, _, index) =>
          editIndex.index === index &&
          editIndex.type === 'edit' &&
          user?.id !== purchaseOrderData?.createId ? (
            <Form.Item
              className="mb-0"
              name="lossWeightThreadAccounting"
              rules={[
                { required: true, message: 'Vui lòng nhập TL' },
                {
                  pattern: /^[^a-zA-Z\u00C0-\u017F]*$/,
                  message: 'Chỉ được nhập số',
                },
              ]}
            >
              <InputForm
                onChange={(e) =>
                  handleWeightChange('lossWeightThreadAccounting', e)
                }
              />
            </Form.Item>
          ) : value ? (
            formatCurrency({
              value: Number(Number(value).toFixed(4)),
              format: 4,
            })
          ) : (
            '-'
          ),
      },
      {
        align: 'right',
        title: 'TL hột ĐC(chỉ)',
        dataIndex: 'includeWeightThreadAccounting',
        // render: (value) => formatNumber(value),
        render: (value, _, index) =>
          editIndex.index === index &&
          editIndex.type === 'edit' &&
          user?.id !== purchaseOrderData?.createId ? (
            <Form.Item
              className="mb-0"
              name="includeWeightThreadAccounting"
              rules={[
                { required: true, message: 'Vui lòng nhập TL' },
                {
                  pattern: /^[^a-zA-Z\u00C0-\u017F]*$/,
                  message: 'Chỉ được nhập số',
                },
              ]}
            >
              <InputForm
                onChange={(e) =>
                  handleWeightChange('includeWeightThreadAccounting', e)
                }
              />
            </Form.Item>
          ) : value ? (
            formatCurrency({
              value: Number(Number(value).toFixed(4)),
              format: 4,
            })
          ) : (
            '-'
          ),
      },
      {
        align: 'right',
        title: 'TL Q10 sau ĐC(chỉ)',
        dataIndex: 'q10Accounting',
        render: (value) =>
          formatCurrency({
            value: Number(Number(value).toFixed(4)),
            format: 4,
          }) || '_',
        // render: (value) => (value ? formatCurrency({ value, format: 3 }) : '-'),
      },
      {
        align: 'right',
        title: 'TL sau ĐC(phân)',
        dataIndex: 'totalWeightStoolAccounting',
        render: (value) =>
          formatCurrency({
            value: Number(Number(value).toFixed(4)),
            format: 4,
          }) || '_',
        // render: (value) => (value ? formatCurrency({ value, format: 3 }) : '-'),
      },
      {
        align: 'right',
        title: 'Thành tiền sau ĐC',
        dataIndex: 'finalPriceAccounting',
        render: (value) => formatNumber(value),
      },
      {
        title: 'Ngày tạo',
        dataIndex: 'createDate',
        render: (value) => formatDate(value),
      },
      {
        width: 180,
        title: 'Người cập nhật',
        dataIndex: ['tmodifyUser', 'fullName'],
        render: (value) => (value ? value : '-'),
      },
      {
        width: 200,
        title: 'Ngày cập nhật',
        dataIndex: 'modifyDate',
        render: (value: string) =>
          value ? dayjs(value).format('DD/MM/YYYY HH:MM') : '-',
      },
      {
        width: 80,
        fixed: 'right',
        align: 'center',
        render: (_, record, index) =>
          editIndex.index === index ? (
            <Space size="middle" className="text-lg">
              <ButtonAction tooltip="Lưu" onClick={handleSave}>
                <SaveTwoTone style={{ fontSize: 20 }} />
              </ButtonAction>
              <ButtonAction tooltip="Huỷ" onClick={() => handleCancel(index)}>
                <CloseCircleTwoTone style={{ fontSize: 20 }} />
              </ButtonAction>
            </Space>
          ) : (
            <Space size="middle" className="text-lg">
              <ButtonAction
                tooltip="Chỉnh sửa"
                disabled={
                  !!purchaseOrderData?.parentId ||
                  !(user?.isAccountant || user?.isStorekeeper || user?.isKTD) ||
                  purchaseOrderData?.statusId === 'OR02_02' ||
                  (migoList &&
                    migoList.length > 0 &&
                    lastMigoStatus === true &&
                    lastMigoType === '501')
                }
                onClick={() => {
                  handleEdit(index, record);
                }}
              >
                <EditIcon style={{ fontSize: 20 }} />
              </ButtonAction>
              <ButtonAction
                tooltip="Xóa"
                onClick={() => handleRemove(record)}
                disabled={
                  !!purchaseOrderData?.parentId ||
                  !(user?.isAccountant || user?.isStorekeeper || user?.isKTD) ||
                  purchaseOrderData?.statusId === 'OR02_02' ||
                  (migoList &&
                    migoList.length > 0 &&
                    lastMigoStatus === true &&
                    lastMigoType === '501')
                }
              >
                <XIcon style={{ fontSize: 20 }} />
              </ButtonAction>
            </Space>
          ),
      },
    ],
    [
      form,
      page,
      user,
      pageSize,
      editIndex,
      materials,
      data,
      purchaseOrderData,
      materialRef,
      measureWeightRef,
      measureWeightAccountantRef,
      migoList,
      lastMigoStatus,
      lastMigoType,
    ],
  );

  const handleCancel = (rowIndex: number) => {
    form.resetFields();

    if (editIndex.type === '') {
      const newData = data?.filter((_, index) => index !== rowIndex);
      setTableData(newData);
    }

    setEditIndex({ index: -1, type: '' });
  };

  const checkFormValidate = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleEdit = (index: number, record: any) => {
    setEditIndex({ index, type: 'edit' });

    // const { includeWeightThread, lossWeightThread, measureWeightThread } =
    //   record;
    const {
      includeWeightThread,
      lossWeightThread,
      measureWeightThread,
      measureWeightThreadAccounting,
      lossWeightThreadAccounting,
      includeWeightThreadAccounting,
      sloc,
    } = record;

    form.setFieldsValue({
      tproductId: record.tproductId,
      includeWeightThread: formatCurrency({
        value: includeWeightThread,
        format: 4,
      })?.toString(),
      lossWeightThread: formatCurrency({
        value: lossWeightThread,
        format: 4,
      })?.toString(),
      measureWeightThread: formatCurrency({
        value: measureWeightThread,
        format: 4,
      })?.toString(),
      includeWeightThreadAccounting: formatCurrency({
        value: includeWeightThreadAccounting,
        format: 4,
      })?.toString(),
      lossWeightThreadAccounting: formatCurrency({
        value: lossWeightThreadAccounting,
        format: 4,
      })?.toString(),
      measureWeightThreadAccounting: formatCurrency({
        value: measureWeightThreadAccounting,
        format: 4,
      })?.toString(),
      sloc: sloc,
    });
  };

  const handleSave = async () => {
    if (!(await checkFormValidate())) {
      form.validateFields();
      return;
    }

    const formValues = form.getFieldsValue(true);
    const selectedProduct = materials.options.find(
      (item) => item.value === formValues.tproductId,
    );

    let productId, productName;
    if (typeof selectedProduct?.label === 'string') {
      const [id, ...nameParts] = selectedProduct.label.split(' - ');
      productId = id;
      productName = nameParts.join(' - ');
    }

    const newItem = {
      tproductId: formValues.tproductId,
      includeWeightThread: formatToNumber(
        formValues?.includeWeightThread?.toString(),
      ),
      lossWeightThread: formatToNumber(
        formValues?.lossWeightThread?.toString(),
      ),
      measureWeightThread: formatToNumber(
        formValues?.measureWeightThread?.toString(),
      ),
      measureWeightThreadAccounting: formatToNumber(
        formValues?.measureWeightThreadAccounting?.toString(),
      ),
      lossWeightThreadAccounting: formatToNumber(
        formValues?.lossWeightThreadAccounting?.toString(),
      ),
      includeWeightThreadAccounting: formatToNumber(
        formValues?.includeWeightThreadAccounting?.toString(),
      ),
      sloc: formValues?.sloc,
      tproduct: {
        id: productId,
        name: productName,
      },
    };

    const newData = [...data];
    if (editIndex.index >= 0) {
      newData[editIndex.index] = {
        ...newData[editIndex.index],
        ...newItem,
      };
    }

    form.resetFields();
    setEditIndex({ index: -1, type: '' });

    // editIndex.type === 'edit' ? onEdit(newData) : onSave(newData);
    // onSave(newData);

    // editIndex.type === '' || user?.id === purchaseOrderData?.createId
    //   ? onSave(newData)
    //   : onEdit(newData);

    // onSave - edit 3 field TL trước ĐC
    // onEdit - edit 3 field TL sau ĐC

    editIndex.type === ''
      ? onSave(newData)
      : user?.id === purchaseOrderData?.createId
        ? onSave(newData)
        : onEdit(newData);
  };

  const handleRemove = (record: any) => {
    const updatedData = [...data];
    const removeData = updatedData.filter((item) => item.id !== record.id);

    setTableData(removeData);
    onDelete(removeData);
  };

  return (
    <Form form={form}>
      <Table
        title={() => (
          <>
            <Flex align="center" justify="space-between">
              <Text strong className="text-lg">
                Mua hàng
              </Text>
              {(user?.isAccountant || user?.isKTD) && (
                <Button
                  type="button"
                  title="Thêm dẻ"
                  iconBefore={<PlusOutlined />}
                  disabled={
                    editIndex.index === 0 ||
                    !!purchaseOrderData?.parentId ||
                    purchaseOrderData?.statusId === 'OR02_02' ||
                    user?.id !== purchaseOrderData?.createId ||
                    (migoList &&
                      migoList.length > 0 &&
                      lastMigoStatus === true &&
                      lastMigoType === '501')
                  }
                  handleOnclick={onAdd}
                />
              )}
            </Flex>
          </>
        )}
        dataSource={data}
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content' }}
        rowKey={(record) => record?.id}
        summary={(pageData) => {
          const data = pageData as TPurchaseDetail[];

          if (data.length <= 0) return;
          const total = totalData(data, totalKeys, dataIndexes);

          return (
            <Summary fixed>
              <Summary.Row>
                <Summary.Cell colSpan={3} index={0}>
                  <Text strong>Tổng cộng</Text>
                </Summary.Cell>
                {dataIndexes.map((dataIndex, index) => {
                  const isCellPrice = dataIndex.includes('Price');

                  return (
                    <Summary.Cell
                      key={dataIndex}
                      index={index + 1}
                      className="font-bold text-right"
                    >
                      {isCellPrice
                        ? formatValue(total?.[dataIndex])
                        : formatCurrency({
                            value: Number(
                              Number(total?.[dataIndex]).toFixed(4),
                            ),
                            format: 4,
                          })}
                    </Summary.Cell>
                  );
                })}
              </Summary.Row>
            </Summary>
          );
        }}
      />
    </Form>
  );
};

export default TablePurchaseInvoiceDetail;
